import { useNavigate, useSearch } from "@tanstack/react-router";
import { Archive, CheckCircle, Clock, CoinsStacked01, Hand, XClose } from "@untitledui/icons";
import { type ReactNode, useCallback, useState } from "react";
import { usePagination } from "@/api/pagination";
import {
  type SampleRequestTab,
  type SampleRequestView,
  useSampleRequestGroups,
  useSampleRequestTabCounts,
} from "@/api/sample-requests";
import { UnboxTableEmptyStateContent } from "@/components/application/table/unbox-table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { GroupCard } from "@/components/collaborators/group-card-collaborators";
import { FilterPills } from "@/components/filters/filter-pills";
import { SampleRequestsFilterPanel } from "@/components/filters/panels";
import { CandidateDetails } from "@/pages/creators/candidate-details";
import { CreatorDetails } from "@/pages/creators/creator-details";
import { FiltersProvider, useFilters } from "@/providers/filter-provider";
import { cx } from "@/utils/cx";
import { SampleBulkActionBar } from "./sample-bulk-action-bar";
import { SampleRequestsControls } from "./sample-requests-controls";
import { SampleSelectionProvider } from "./sample-selection-context";

const TABS: { id: SampleRequestTab; label: string; icon: ReactNode; showCount: boolean }[] = [
  { id: "pending", showCount: true, label: "Pending", icon: <Clock className="size-4" /> },
  { id: "approved", showCount: true, label: "Approved", icon: <CheckCircle className="size-4" /> },
  { id: "rejected", showCount: true, label: "Rejected", icon: <XClose className="size-4" /> },
  { id: "ignored", showCount: false, label: "Ignored", icon: <Archive className="size-4" /> },
  { id: "expired", showCount: false, label: "Expired", icon: <Clock className="size-4" /> },
];

const GROUP_PAGE_SIZE = 20;

function SampleRequestsTableContent() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    view?: SampleRequestView;
    tab?: SampleRequestTab;
  };
  const view: SampleRequestView = search.view ?? "product";
  const tab: SampleRequestTab = search.tab ?? "pending";

  const { apiFilters, reset, hasActiveFilters } = useFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [allocationsOpen, setAllocationsOpen] = useState(true);
  const pagination = usePagination({ offset: 0, limit: GROUP_PAGE_SIZE });
  const { setPage } = pagination;
  const [selectedCreatorId, setSelectedCreatorId] = useState<string>();
  const [selectedCandidateId, setSelectedCandidateId] = useState<string>();

  const handleSelectCreator = useCallback((creator: { creatorId?: string; tiktokOpenId: string }) => {
    if (creator.creatorId) {
      setSelectedCreatorId(creator.creatorId);
      setSelectedCandidateId(undefined);
    } else {
      setSelectedCandidateId(creator.tiktokOpenId);
      setSelectedCreatorId(undefined);
    }
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setProductSearch(value);
      setPage(1);
    },
    [setPage],
  );

  const { data: counts } = useSampleRequestTabCounts();
  const { data, isLoading } = useSampleRequestGroups(
    { view, tab, search: productSearch || undefined, filters: apiFilters },
    pagination.pagination,
  );
  const groups = data?.items ?? [];
  const hasNext = groups.length === pagination.pagination.limit;
  const showControls = isLoading || groups.length > 0 || productSearch !== "" || hasActiveFilters;

  const setView = (next: SampleRequestView) => {
    navigate({
      to: ".",
      search: (prev: { view: SampleRequestView; tab: SampleRequestTab }) => ({
        ...prev,
        view: next,
      }),
      replace: true,
    });
  };
  const setTab = (next: SampleRequestTab) => {
    pagination.setPage(1);
    navigate({
      to: ".",
      search: (prev: { view: SampleRequestView; tab: SampleRequestTab }) => ({
        ...prev,
        tab: next,
      }),
      replace: true,
    });
  };

  return (
    <>
      <div className={cx("flex flex-col gap-5 transition-all duration-300")}>
        {showControls && (
          <SampleRequestsControls
            view={view}
            onViewChange={setView}
            onSearchChange={handleSearchChange}
            onFilterClick={() => setFiltersOpen((prev) => !prev)}
          />
        )}

        <div className="flex flex-row flex-wrap items-center gap-1 border-b border-secondary">
          {TABS.map((t) => {
            const isActive = t.id === tab;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cx(
                  "-mb-px flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors",
                  isActive
                    ? "border-brand-solid text-brand-secondary"
                    : "border-transparent text-quaternary hover:text-tertiary",
                )}
              >
                {t.icon}
                {t.label}
                {t.showCount && counts?.[t.id] ? (
                  <Badge type="pill-color" size="sm" color={isActive ? "brand" : "gray"}>
                    {counts[t.id]}
                  </Badge>
                ) : null}
              </button>
            );
          })}
          {tab === "approved" && !allocationsOpen && (
            <Button
              color="secondary"
              size="sm"
              className="my-1 ml-auto"
              iconLeading={<CoinsStacked01 className="size-4" />}
              onClick={() => setAllocationsOpen(true)}
            >
              Monthly allocations
            </Button>
          )}
        </div>

        <FilterPills className="-my-2" />

        <SampleBulkActionBar />

        <div className="flex flex-row gap-5">
          <div className="flex min-w-0 flex-1 flex-col gap-5">
            <div className="flex flex-col gap-3">
              {isLoading ? (
                <p className="py-10 text-center text-sm text-tertiary">Loading…</p>
              ) : groups.length === 0 ? (
                <div className="py-16">
                  <UnboxTableEmptyStateContent
                    rows={[]}
                    hasActiveFilters={hasActiveFilters}
                    emptyState={{
                      icon: Hand,
                      title: "No Sample Requests",
                      description: "As creators request samples, they'll appear here for you to action.",
                    }}
                    filteredEmptyState={{
                      icon: Hand,
                      title: "No requests match your filters",
                      description: "Try adjusting your filters to see more results.",
                      onClearFilters: reset,
                    }}
                  />
                </div>
              ) : (
                groups.map((group) => (
                  <GroupCard
                    key={group.groupId}
                    group={group}
                    view={view}
                    tab={tab}
                    filters={apiFilters}
                    onSelectCreator={handleSelectCreator}
                  />
                ))
              )}
            </div>

            {(pagination.page > 1 || hasNext) && (
              <div className="flex items-center justify-center gap-3 py-2">
                <Button
                  color="secondary"
                  size="sm"
                  isDisabled={pagination.page <= 1}
                  onClick={() => {
                    pagination.prevPage();
                  }}
                >
                  Previous
                </Button>
                <span className="text-sm text-tertiary">Page {pagination.page}</span>
                <Button
                  color="secondary"
                  size="sm"
                  isDisabled={!hasNext}
                  onClick={() => {
                    pagination.nextPage();
                  }}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <SampleRequestsFilterPanel open={filtersOpen} close={() => setFiltersOpen(false)} clearAll={reset} />

      <CreatorDetails id={selectedCreatorId} onClose={() => setSelectedCreatorId(undefined)} />
      <CandidateDetails id={selectedCandidateId} onClose={() => setSelectedCandidateId(undefined)} />
    </>
  );
}

const ACTIVE_FIELDS = ["product", "collaboration", "sampleBrandSafety", "minPostRate", "maxPostRate"] as const;

export function SampleRequestsTable() {
  return (
    <FiltersProvider activeFields={[...ACTIVE_FIELDS]}>
      <SampleSelectionProvider>
        <SampleRequestsTableContent />
      </SampleSelectionProvider>
    </FiltersProvider>
  );
}
